import React, { useCallback, useMemo, useState } from 'react';
import {
  SupportContext,
  type SupportContextValue,
} from './supportContextValue';

export const SupportContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [context, setContextState] = useState<SupportContextValue>({});
  // Context supplied by whoever opened the modal (e.g. a failed OASIS export),
  // layered over the ambient page context and discarded on close so the page's
  // own context survives.
  const [overlay, setOverlay] = useState<SupportContextValue | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const setContext = useCallback((ctx: SupportContextValue) => {
    setContextState(ctx);
  }, []);

  const clearContext = useCallback(() => {
    setContextState({});
  }, []);

  const openSupport = useCallback((ctx?: SupportContextValue) => {
    setOverlay(ctx ?? null);
    setIsOpen(true);
  }, []);

  const closeSupport = useCallback(() => {
    setIsOpen(false);
    setOverlay(null);
  }, []);

  const value = useMemo(
    () => ({
      // The opener wins on subject/label/recipient, but hidden context is
      // merged key-wise — the page's context (which intake, which patient) is
      // still what makes the request diagnosable.
      context: overlay
        ? {
            ...context,
            ...overlay,
            hiddenContext: {
              ...context.hiddenContext,
              ...overlay.hiddenContext,
            },
          }
        : context,
      setContext,
      clearContext,
      isOpen,
      openSupport,
      closeSupport,
    }),
    [
      context,
      overlay,
      setContext,
      clearContext,
      isOpen,
      openSupport,
      closeSupport,
    ]
  );

  return (
    <SupportContext.Provider value={value}>{children}</SupportContext.Provider>
  );
};
